/* eslint-disable @typescript-eslint/no-unused-vars */
import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';
import {Linking, ScrollView, StyleSheet, View} from 'react-native';
import {
  Button,
  Caption,
  DataTable,
  List,
  Modal,
  Portal,
  Searchbar,
  Text,
  Title,
  TouchableRipple,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useMutation, useQueryClient} from 'react-query';
import {AlertsProvider, useAlerts} from 'react-native-paper-alerts';
import {useMemo, useState, Profiler, useCallback, memo, useEffect} from 'react';
import {useLinkTo} from '@react-navigation/native';

import {useAppSettings} from '../components/AppSettings';
import {
  GuestyCustomField,
  GuestyReservation,
  resyncGuestyReservation,
  sync,
  updateGuestyReservation,
  useGuestyCustomFields,
  useGuestyReservations,
} from '../hooks/useGuestyData';
import {
  Fund,
  LimitedPartner,
  minimumNumberSearchString,
  minimumTextSearchString,
  searchLimitedPartners,
  shouldSearch,
  useFunds,
  useProperties,
  useReservationYears,
  useSubscribers,
} from '../hooks/useDomainData';
import {LimitedPartnerComponent} from './LimitedPartners';
import {useLayoutInfo} from '../hooks/useLayoutInfo';

export type guestyCustomFieldIdToFundMap = {
  [id: string]: {fund: Fund; customField: GuestyCustomField};
};

export const getCreditsUsedInfo = (
  res: GuestyReservation,
  fieldMap: guestyCustomFieldIdToFundMap,
): {credits: number; fund: Fund}[] | undefined => {
  let creditsUsed: {credits: number; fund: Fund}[] | undefined;
  for (const field of res.customFields) {
    for (const mappedFieldId of Object.keys(fieldMap)) {
      // match if matching custom field for the fund / credits used,
      if (mappedFieldId === field.fieldId) {
        // 1- ignore zero credit entries, they are never used downstream
        // 2- except in the case of cancels where we pass zeros through as an explicit
        //    signal that this reservation was canceled but there is no charge
        if (field.value === 0 && res.status !== 'canceled') {
          continue;
        }

        if (creditsUsed === undefined) {
          creditsUsed = [];
        }

        creditsUsed.push({
          credits: field.value,
          fund: fieldMap[mappedFieldId].fund,
        });
      }
    }
  }

  return creditsUsed;
};

export function GuestyUnmatchedReservations() {
  return <GuestyReservationTable matched={false} />;
}

export function GuestyMatchedReservations() {
  return <GuestyReservationTable matched={true} />;
}

function GuestyReservationTable({matched}: {matched: boolean}) {
  const appSettings = useAppSettings();
  const layoutInfo = useLayoutInfo();
  const Alert = useAlerts();
  const guestyReservations = useGuestyReservations();
  const guestyCustomFields = useGuestyCustomFields();
  const reservationYears = useReservationYears();
  const subscribers = useSubscribers();
  const funds = useFunds();
  const properties = useProperties();
  const queryClient = useQueryClient();
  const linkTo = useLinkTo();

  const [accordianState, setAccordianState] = useState<boolean[]>([]);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editReservation, setEditReservation] = useState<
    GuestyReservation | undefined
  >(undefined);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchFund, setSearchFund] = useState<Fund | undefined>(undefined);
  const searchQueryLower = searchQuery.toLowerCase();
  const onChangeSearch = (query: string) => setSearchQuery(query);

  useEffect(() => {
    if (reservationYears !== undefined) {
      const initialAccordianState = Array(reservationYears.length).fill(false);
      initialAccordianState[0] = true;
      setAccordianState(initialAccordianState);
    }
  }, [reservationYears]);

  const loading =
    !funds ||
    !subscribers ||
    !guestyCustomFields ||
    !guestyReservations ||
    !reservationYears;

  const {limitedPartners, limitedPartnerFundMap} = useMemo(() => {
    const filteredLimitedPartners: LimitedPartner[] = [];
    const lpFundMap: {[key: string]: Fund} = {};
    if (subscribers !== undefined) {
      console.log('Subscriber count pre-filtering ' + subscribers.length);
      const filteredSubs = subscribers.filter(sub => {
        return sub.fund?.acceptingReservations === true;
      });
      console.log('Subscriber count filtered ' + filteredSubs.length);

      filteredSubs.forEach(sub => {
        sub.limitedPartners?.forEach(lp => {
          filteredLimitedPartners.push(lp);
          lpFundMap[lp.ref.id] = sub.fund!;
        });
      });
    }
    return {
      limitedPartners: filteredLimitedPartners,
      limitedPartnerFundMap: lpFundMap,
    };
  }, [subscribers]);

  const handleUnmatchLimitedPartners = async (guestyRes: GuestyReservation) => {
    console.log(`unmatch LPs from res ${guestyRes._id}`);
    const limitedPartnerMap = guestyRes.limitedPartnerMap;
    if (!limitedPartnerMap) {
      console.warn(`No LP for ${guestyRes._id}?`);
      return;
    }

    for (const lpFundEntry of limitedPartnerMap) {
      console.log(
        `removing ${guestyRes._id} from ${lpFundEntry.lp.limitedPartnerName}`,
      );
      lpFundEntry.lp.guestyReservationIds =
        lpFundEntry.lp.guestyReservationIds?.filter(
          resId => resId !== guestyRes._id,
        );
      saveLimitedPartnerReservationIds.mutate(lpFundEntry.lp);
    }
    await updateGuestyReservation(guestyRes);
  };

  const handleUnmatchReservation = async (res: GuestyReservation) => {
    console.log(`unmatching guesty res: ${res._id}`);

    // if the reservation was a direct match, clear it from LP
    // if (res.limitedPartner?.guestyReservationIds?.find(id => id === res._id)) {
    //   console.log('unmatching guesty res with manual LP match');
    //   // TODO - remove this reservationId from the set, and update LP

    //   // TODO - invalidate limited partners from query client
    // }

    // clean out things we do not want to store
    delete res.creditsUsedMap;
    delete res.limitedPartnerMap;
    delete res.property;
    delete res.formattedStayDates;

    // clear out the local reservation reference to indicate we are unmatching
    delete res.reservationRef;
    delete res.reservationRefPath;

    await updateGuestyReservation(res);
    await queryClient.invalidateQueries('guestyReservations');
  };

  const handleRefreshTransactions = async () => {
    console.log('resynchronize');
    // 1- resynchronize from guesty to firestore
    try {
      await sync('reservationSync');
    } catch (e) {
      Alert.alert('Error', '' + e, [{text: appSettings.t('OK')}]);
    } finally {
      await queryClient.invalidateQueries('syncAdapterStatus');
    }

    // 2- invalidate the local cache so we go back to "loading"
    await queryClient.invalidateQueries('guestyReservations');
    console.log('done with resynchronize');
  };

  const handleRefreshTransaction = async (
    guestyReservation: GuestyReservation,
  ) => {
    console.log(`resync guesty reservation ${guestyReservation._id}`);
    // 1- resynchronize from guesty to firestore
    try {
      await resyncGuestyReservation(guestyReservation._id);
    } catch (e) {
      Alert.alert('Error', '' + e, [{text: appSettings.t('OK')}]);
    }

    // 2- invalidate the local cache so we go back to "loading"
    await queryClient.invalidateQueries('guestyReservations');
    console.log('done with single res resync');
  };

  const onRender = (
    id: string,
    phase: string,
    actualDuration: number,
    baseDuration: number,
    startTime: number,
    commitTime: number,
  ) => {
    console.log(
      `onRender ${id} ${phase} actual ${actualDuration.toFixed(
        2,
      )} base ${baseDuration.toFixed(2)} start ${startTime.toFixed(
        2,
      )} commit ${commitTime.toFixed(2)}`,
    );
  };

  // Get the credit field ids that map to credits used
  // FIXME make this an API call and re-use cloud function definition
  // be mindful of react-query cache invalidation
  const fieldFundMap: guestyCustomFieldIdToFundMap = useMemo(() => {
    const map: guestyCustomFieldIdToFundMap = {};
    for (const fund of funds ?? []) {
      // - get funds with 'guestyCreditCustomFieldName' !== null
      if (fund.guestyCreditCustomFieldName) {
        // find the correct custom field name so we can get the custom field id
        const customField = (guestyCustomFields ?? []).find(
          field => field.displayName === fund.guestyCreditCustomFieldName,
        );
        // - create map of funds <-> custom field (with id + name)
        if (customField) {
          map[customField._id] = {fund, customField};
        }
      }
    }
    return map;
  }, [funds, guestyCustomFields]);

  // Filter/sort/augment reservations to prep them for display:
  const filteredReservations: GuestyReservation[] = useMemo(() => {
    // if we've got now limited partners, no following work is useful
    // this loop will execute again once we have them
    if (limitedPartners === undefined) {
      return [];
    }

    const millisInAdvanceForRefund = 1000 * 60 * 60 * 24 * 179; // 179 days in millis
    const returnReservations: GuestyReservation[] =
      // for reservation matching purposes, we:
      // - filter outreservations that were cancelled in time for a refund
      guestyReservations?.filter(res => {
        // Temporary override to focus for specific guest troubleshooting
        // if (res.guest.fullName !== 'Brian Craft') {
        //   return false;
        // }

        if (
          res.status === 'canceled' &&
          res.canceledAt !== 0 &&
          res.canceledAt + millisInAdvanceForRefund < res.checkIn
        ) {
          // console.log(`res ${res._id} canceled in time for full refund. skip`);
          return false;
        }

        // - remaining cancels were late, but a zero total is explicit signal from guest
        //   relations that there is no charge for some reason
        if (res.status === 'canceled') {
          const creditsUsedMap = getCreditsUsedInfo(res, fieldFundMap);
          const totalCreditsUsed =
            creditsUsedMap?.reduce(
              (sum, mapEntry) => sum + mapEntry.credits,
              0,
            ) ?? 0;
          if (creditsUsedMap !== undefined && totalCreditsUsed === 0) {
            // console.log(`res ${res._id} canceled late but zeroed. skip`);
            return false;
          }
        }
        // console.log(`res ${res._id} canceled late not zeroed. charging.`);

        return true;
      }) ?? [];

    // populate (if possible) fund+credits, LP(s), property, short checkin/checkout date
    returnReservations.forEach(res => {
      // for each custom field see if it is one mapped to a funds credit field
      res.creditsUsedMap = getCreditsUsedInfo(res, fieldFundMap);

      // Get the maybe-empty set of LPs for this GuestyGuest and GuestyReservation
      const lPsByGuestyGuest: LimitedPartner[] = [];
      const lPsByGuestyRes: LimitedPartner[] = [];
      limitedPartners.forEach(lp => {
        if (lp.guestyGuestIds?.includes(res.guest._id)) {
          lPsByGuestyGuest.push(lp);
        }
        if (lp.guestyReservationIds?.includes(res._id)) {
          lPsByGuestyRes.push(lp);
        }
      });

      if (res.source.includes('Space Available')) {
        // space available? take the first LP that matches, arbitrary is fine
        if (lPsByGuestyGuest.length > 0) {
          res.limitedPartnerMap = [
            {
              fundCode:
                limitedPartnerFundMap[lPsByGuestyGuest[0].ref.id].fundCode,
              lp: lPsByGuestyGuest[0],
            },
          ];
          res.creditsUsedMap = [
            {
              fund: limitedPartnerFundMap[lPsByGuestyGuest[0].ref.id],
              credits: 0,
            },
          ];
        }
        if (res.limitedPartnerMap === undefined && lPsByGuestyRes.length > 0) {
          res.limitedPartnerMap = [
            {
              fundCode:
                limitedPartnerFundMap[lPsByGuestyRes[0].ref.id].fundCode,
              lp: lPsByGuestyRes[0],
            },
          ];
          res.creditsUsedMap = [
            {
              fund: limitedPartnerFundMap[lPsByGuestyRes[0].ref.id],
              credits: 0,
            },
          ];
        }
        if (
          res.limitedPartnerMap !== undefined &&
          res.limitedPartnerMap.length > 0
        ) {
          // console.log(
          //   `res ${res._id} sp.avail. lp ${res.limitedPartnerMap[0].lp.limitedPartnerName} fund ${res.limitedPartnerMap[0].fundCode}`,
          // );
        }
      } else {
        // not space available? try to find an LP:Fund match for each credit:Fund entry
        // console.log(
        //   `res ${res._id} looking for ${
        //     res.creditsUsedMap?.length ?? 0
        //   } LP:fund mappings`,
        // );
        res.creditsUsedMap?.forEach(creditsUsedMapEntry => {
          // do we have an LP for this credit:fund entry in LP:GuestyGuests map?
          let maybeLP = lPsByGuestyGuest.find(
            lp =>
              limitedPartnerFundMap[lp.ref.id].fundCode ===
              creditsUsedMapEntry.fund.fundCode,
          );
          if (maybeLP !== undefined) {
            // console.log(
            //   `  ...found LP by Guest ${maybeLP.limitedPartnerName} (${creditsUsedMapEntry.fund.fundCode}:${creditsUsedMapEntry.credits})`,
            // );
          }
          // maybe we have an LP for this credit:fund entry in LP:GuestyReservations?
          if (maybeLP === undefined) {
            maybeLP = lPsByGuestyRes.find(
              lp =>
                limitedPartnerFundMap[lp.ref.id].fundCode ===
                creditsUsedMapEntry.fund.fundCode,
            );
            if (maybeLP !== undefined) {
              // console.log(
              //   `  ...found LP by Res ${maybeLP.limitedPartnerName} (${creditsUsedMapEntry.fund.fundCode}:${creditsUsedMapEntry.credits})`,
              // );
            }
          }
          // if we found a match, init the map as needed and push it in
          if (maybeLP !== undefined) {
            if (res.limitedPartnerMap === undefined) {
              res.limitedPartnerMap = [];
            }
            res.limitedPartnerMap.push({
              fundCode: creditsUsedMapEntry.fund.fundCode,
              lp: maybeLP,
            });
            // console.log(
            //   `  ......adding LP ${maybeLP.limitedPartnerName} for fund ${creditsUsedMapEntry.fund.fundCode}`,
            // );
          }
        });
      }

      // lookup listing <-> Property
      res.property = properties?.find(
        property => res.listing._id === property.guestyId,
      );

      const checkInDate = new Date(res.checkIn);
      const checkOutDate = new Date(res.checkOut);
      res.formattedStayDates = `${
        checkInDate.getMonth() + 1
      }/${checkInDate.getDate()}-${
        checkOutDate.getMonth() + 1
      }/${checkOutDate.getDate()}/${checkOutDate.getFullYear()}`;
    });

    // Sort things so in order, zero credits, no property, no LP, alphabetic:
    returnReservations.sort((a, b) => {
      const aTotalCredits =
        a.creditsUsedMap?.reduce(
          (sum, mapEntry) => sum + mapEntry.credits,
          0,
        ) ?? 0;
      const bTotalCredits =
        b.creditsUsedMap?.reduce(
          (sum, mapEntry) => sum + mapEntry.credits,
          0,
        ) ?? 0;
      // - zero credits are on top unless 'Space Available', sorted by name if both zero
      if (
        aTotalCredits === 0 &&
        !a.source.includes('Space Available') &&
        bTotalCredits === 0 &&
        !b.source.includes('Space Available')
      ) {
        return a.guest.lastName.localeCompare(b.guest.lastName);
      }
      if (aTotalCredits === 0 && !a.source.includes('Space Available')) {
        return -1;
      }
      if (bTotalCredits === 0 && !b.source.includes('Space Available')) {
        return 1;
      }

      // - unmatched properties are next block
      if (!a.property && !b.property) {
        return a.guest.lastName.localeCompare(b.guest.lastName);
      }
      if (!a.property) {
        return -1;
      }
      if (!b.property) {
        return 0;
      }

      // - unmatched guests are next block
      if (!a.limitedPartnerMap && !b.limitedPartnerMap) {
        return a.guest.lastName.localeCompare(b.guest.lastName);
      }
      if (!a.limitedPartnerMap) {
        return -1;
      }
      if (!b.limitedPartnerMap) {
        return 0;
      }

      // - everyone else after, sorted alphabetic
      return a.guest.lastName.localeCompare(b.guest.lastName);
    });

    console.debug(
      `current reservations: ${JSON.stringify(returnReservations, null, 2)}`,
    );

    return returnReservations;
  }, [
    properties,
    limitedPartnerFundMap,
    limitedPartners,
    fieldFundMap,
    guestyReservations,
  ]);

  // split into matched and unmatched
  const {matchedGuestyReservations, unmatchedGuestyReservations} =
    useMemo(() => {
      const matchedRes: GuestyReservation[] = [];
      const unmatchedRes: GuestyReservation[] = [];
      for (const guestyReservation of filteredReservations) {
        if (guestyReservation.reservationRef !== undefined) {
          matchedRes.push(guestyReservation);
        } else {
          unmatchedRes.push(guestyReservation);
        }
      }
      return {
        matchedGuestyReservations: matchedRes,
        unmatchedGuestyReservations: unmatchedRes,
      };
    }, [filteredReservations]);

  const saveLimitedPartnerReservationIds = useMutation(
    (limitedPartner: LimitedPartner) => {
      console.log('in saveLimitedPartnerReservationIds');

      console.log(
        `going to save: ${JSON.stringify(
          limitedPartner.guestyReservationIds,
        )} into doc ${limitedPartner.ref.id}`,
      );

      return firestore()
        .collection('limitedPartners')
        .doc(limitedPartner.ref.id)
        .update({
          guestyReservationIds: limitedPartner.guestyReservationIds,
        });
    },
    {
      onSettled: () => {
        console.log('save succeeded');
        return queryClient.invalidateQueries([
          'limitedPartners',
          'subscribers',
        ]);
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const searchedLimitedPartners = useMemo(
    () => searchLimitedPartners(searchQueryLower, searchFund, limitedPartners),
    [searchQueryLower, searchFund, limitedPartners],
  );

  const themedStyles = useMemo(
    () =>
      StyleSheet.create({
        modalContent: {
          margin: 60,
          padding: 20,
          borderColor: appSettings.currentTheme.colors.border,
          borderWidth: 2,
          backgroundColor: appSettings.currentTheme.colors.background,
          position: 'absolute',
          maxWidth: layoutInfo.screen.width - 120,
          maxHeight: layoutInfo.screen.height - 160,
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
      }),
    [appSettings.currentTheme.colors, layoutInfo.screen],
  );

  const ManuallyMatchLPButton = memo(function ManuallyMatchLPButton({
    reservation,
    color,
    index = 0, // which payment to match, if split payments. Zero is single payment to match.
  }: {
    reservation: GuestyReservation;
    color: string;
    index: number;
  }) {
    const openLPMatchModal = useCallback(() => {
      setEditReservation(reservation);
      setSearchQuery(reservation.guest.lastName);
      setSearchFund(/* FIXME need to get fund to match this time */ undefined);
      setEditDialogVisible(true);
    }, [reservation]);

    return (
      <TouchableRipple onPress={openLPMatchModal}>
        <View style={styles.row}>
          <Text style={{color}}>LP{index > 0 ? index + 1 : ''}</Text>
          <Icon color={color} name="link" size={20} />
        </View>
      </TouchableRipple>
    );
  });

  const saveLPReservationMatch = async (lp: LimitedPartner) => {
    if (editReservation === undefined) {
      return;
    }
    console.log(`match lp ${lp.limitedPartnerName}`);
    const newLP = {
      guestyReservationIds: lp.guestyReservationIds,
      ref: lp.ref,
    };
    if (!newLP.guestyReservationIds) {
      newLP.guestyReservationIds = [];
    }
    if (!newLP.guestyReservationIds.includes(editReservation._id)) {
      newLP.guestyReservationIds.push(editReservation._id);
      saveLimitedPartnerReservationIds.mutate(newLP as LimitedPartner);
      setSearchQuery('');
    }
    setEditDialogVisible(false);
    await updateGuestyReservation(editReservation);
    await queryClient.invalidateQueries('guestyReservations');
    // FIXME did not refresh the page? after save hmm
  };

  const ManuallyUnmatchLPButton = memo(function ManuallyUnmatchLPButton({
    reservation,
    color,
    index = 0, // which payment to unmatch, if split payments. Zero is single payment to match.
  }: {
    reservation: GuestyReservation;
    color: string;
    index: number;
  }) {
    const unMatchLP = useCallback(async () => {
      await handleUnmatchLimitedPartners(reservation);
    }, [reservation]);

    return (
      <TouchableRipple onPress={unMatchLP}>
        <View style={styles.row}>
          <Text style={{color}}>LP{index > 0 ? index + 1 : ''}</Text>
          <Icon color={color} name="link-off" size={20} />
        </View>
      </TouchableRipple>
    );
  });

  const RefreshTransactionButton = memo(function RefreshTransactionButton({
    reservation,
    color,
  }: {
    reservation: GuestyReservation;
    color: string;
  }) {
    const refreshTransaction = useCallback(async () => {
      await handleRefreshTransaction(reservation);
    }, [reservation]);
    return (
      <TouchableRipple onPress={refreshTransaction}>
        <View style={styles.row}>
          <Icon
            style={styles.horizontalPad}
            color={color}
            name="refresh"
            size={20}
          />
        </View>
      </TouchableRipple>
    );
  });

  const LinkToGuestyGuestButton = memo(function LinkToGuestyGuestButton({
    reservation,
    color,
  }: {
    reservation: GuestyReservation;
    color: string;
  }) {
    const openGuesty = useCallback(() => {
      Linking.openURL(
        `https://app.guesty.com/people/contact/${reservation.guest._id}/profile`,
      );
    }, [reservation]);
    return (
      <TouchableRipple onPress={openGuesty}>
        <Caption style={{color}}>Guesty</Caption>
      </TouchableRipple>
    );
  });

  const LinkToGuestyReservationButton = memo(
    function LinkToGuestyReservationButton({
      reservation,
      color,
    }: {
      reservation: GuestyReservation;
      color: string;
    }) {
      const openGuesty = useCallback(() => {
        Linking.openURL(
          `https://app.guesty.com/reservations/${reservation._id}/summary`,
        );
      }, [reservation]);
      return (
        <TouchableRipple onPress={openGuesty}>
          <View style={styles.row}>
            <Caption style={{color}}>Guesty</Caption>
          </View>
        </TouchableRipple>
      );
    },
  );

  const LinkToLimitedPartnerButton = memo(function LinkToLimitedPartnerButton({
    reservation,
    color,
  }: {
    reservation: GuestyReservation;
    color: string;
  }) {
    const openLP = useCallback(() => {
      linkTo(
        // WIP new link algo - for all Members in the LPs, find a common one and link that
        '/investor/limited-partner/' +
          reservation.limitedPartnerMap!![0].lp.limitedPartnerId,
      );
    }, [reservation]);
    return (
      <TouchableRipple onPress={openLP}>
        <Caption style={{color}}>Portal</Caption>
      </TouchableRipple>
    );
  });

  const GuestyReservationRow = memo(function GuestyReservationRow({
    guestyReservation,
  }: {
    guestyReservation: GuestyReservation;
  }) {
    return (
      <DataTable.Row>
        <DataTable.Cell style={styles.widerCell}>
          {guestyReservation.guest.fullName} (
          <View style={styles.row}>
            <LinkToGuestyGuestButton
              reservation={guestyReservation}
              color={appSettings.currentTheme.colors.primary}
            />
            <Caption
              style={{
                color: appSettings.currentTheme.colors.text,
              }}>
              {' / '}
            </Caption>
            {guestyReservation.limitedPartnerMap &&
              guestyReservation.creditsUsedMap &&
              guestyReservation.limitedPartnerMap.length ===
                guestyReservation.creditsUsedMap.length && (
                <LinkToLimitedPartnerButton
                  reservation={guestyReservation}
                  color={appSettings.currentTheme.colors.primary}
                />
              )}
            {(!guestyReservation.limitedPartnerMap ||
              !guestyReservation.creditsUsedMap ||
              guestyReservation.limitedPartnerMap.length !==
                guestyReservation.creditsUsedMap.length) && (
              <Caption
                // eslint-disable-next-line react-native/no-inline-styles
                style={{
                  color: 'red',
                }}>
                LP Issue
              </Caption>
            )}
          </View>
          )
        </DataTable.Cell>
        <DataTable.Cell style={styles.narrowCell}>
          {guestyReservation.formattedStayDates}
        </DataTable.Cell>
        <DataTable.Cell style={styles.widerCell}>
          <Text
            // eslint-disable-next-line react-native/no-inline-styles
            style={{
              color: guestyReservation.property
                ? appSettings.currentTheme.colors.primary
                : 'red',
            }}>
            {guestyReservation.property?.propertyName ??
              `Unmatched ('${guestyReservation.listing.nickname}')`}
          </Text>
        </DataTable.Cell>
        <DataTable.Cell style={styles.mediumCell}>
          <Text
            // eslint-disable-next-line react-native/no-inline-styles
            style={{
              color:
                guestyReservation.creditsUsedMap ||
                guestyReservation.source.includes('Space Available')
                  ? appSettings.currentTheme.colors.primary
                  : 'red',
            }}>
            {guestyReservation.creditsUsedMap?.reduce(
              (fundCodes, mapEntry) =>
                fundCodes === ''
                  ? mapEntry.fund.fundCode
                  : `${fundCodes}+${mapEntry.fund.fundCode}`,

              '',
            ) ??
              (guestyReservation.source.includes('Space Available')
                ? 'Sp.Avail'
                : 'None')}
            {''}/{guestyReservation.status === 'canceled' ? 'canc./' : ''}
            {guestyReservation.source.includes('Space Available')
              ? 'Sp.Avail.'
              : guestyReservation.creditsUsedMap?.reduce(
                  (sum, mapEntry) => sum + mapEntry.credits,
                  0,
                ) ?? 0}
          </Text>
        </DataTable.Cell>
        <DataTable.Cell style={styles.narrowCell}>
          <LinkToGuestyReservationButton
            reservation={guestyReservation}
            color={appSettings.currentTheme.colors.primary}
          />
        </DataTable.Cell>
        <DataTable.Cell style={styles.mediumCell}>
          <RefreshTransactionButton
            reservation={guestyReservation}
            color={appSettings.currentTheme.colors.primary}
          />
          {!guestyReservation.limitedPartnerMap && (
            <ManuallyMatchLPButton
              reservation={guestyReservation}
              color={appSettings.currentTheme.colors.primary}
              index={0}
            />
          )}
          {guestyReservation.limitedPartnerMap &&
            guestyReservation.limitedPartnerMap[0].lp.guestyReservationIds?.find(
              resId => resId === guestyReservation._id,
            ) && (
              <ManuallyUnmatchLPButton
                reservation={guestyReservation}
                color={appSettings.currentTheme.colors.primary}
                index={0}
              />
            )}
        </DataTable.Cell>
      </DataTable.Row>
    );
  });

  if (!reservationYears) {
    return <Text style={styles.title}>Matched Guesty Reservations</Text>;
  }

  return (
    <ScrollView>
      {loading && <Title style={styles.title}>Loading...</Title>}
      {!loading &&
        reservationYears.map((year, index) => {
          year.endJSDate = (
            year.endDate as FirebaseFirestoreTypes.Timestamp
          ).toDate();
          year.startJSDate = (
            year.startDate as FirebaseFirestoreTypes.Timestamp
          ).toDate();
          const unmatchedReservations = unmatchedGuestyReservations.filter(
            reservation =>
              reservation.checkIn < year.endJSDate!.valueOf() &&
              reservation.checkIn > year.startJSDate!.valueOf(),
          );
          const mmatchedReservations = matchedGuestyReservations.filter(
            reservation =>
              reservation.checkIn < year.endJSDate!.valueOf() &&
              reservation.checkIn > year.startJSDate!.valueOf(),
          );
          return (
            <>
              <List.Accordion
                key={year.name}
                title={`Reservation Year ${year.name}`}
                onPress={() => {
                  const newAccordianState = Array.from(accordianState);
                  newAccordianState[index] = !newAccordianState[index];
                  setAccordianState(newAccordianState);
                }}
                expanded={accordianState[index]}>
                <View style={styles.horizontalCentered}>
                  {!matched && (
                    <Title style={styles.title}>
                      {year.name} Unmatched Reservations (
                      {unmatchedReservations.length}:{' '}
                      {
                        unmatchedReservations.filter(
                          res =>
                            (res.creditsUsedMap === undefined ||
                              res.creditsUsedMap.reduce(
                                (sum, mapEntry) => sum + mapEntry.credits,
                                0,
                              ) === 0) &&
                            !res.source.includes('Space Available'),
                        ).length
                      }{' '}
                      no credits,{' '}
                      {
                        unmatchedReservations.filter(res => !res.property)
                          .length
                      }{' '}
                      no property,{' '}
                      {
                        unmatchedReservations.filter(
                          res => !res.limitedPartnerMap,
                        ).length
                      }{' '}
                      needs LP)
                    </Title>
                  )}
                  {matched && (
                    <Title style={styles.title}>
                      {year.name} Matched Reservations
                    </Title>
                  )}
                  <Button
                    mode="outlined"
                    onPress={async () => await handleRefreshTransactions()}>
                    Refresh from Guesty
                  </Button>
                </View>

                <Profiler
                  key={year.name}
                  id={`Reservation Table ${year.name}`}
                  onRender={onRender}>
                  <ScrollView
                    persistentScrollbar={true}
                    style={[styles.container, styles.content]}>
                    <DataTable>
                      {loading && (
                        <DataTable.Row>
                          <DataTable.Header>
                            <DataTable.Title>loading...</DataTable.Title>
                          </DataTable.Header>
                        </DataTable.Row>
                      )}
                      {!loading && (
                        <DataTable.Header style={styles.shortHeader}>
                          <DataTable.Title style={styles.widerCell}>
                            Guest
                          </DataTable.Title>
                          <DataTable.Title style={styles.narrowCell}>
                            Dates
                          </DataTable.Title>
                          <DataTable.Title style={styles.widerCell}>
                            Property
                          </DataTable.Title>
                          <DataTable.Title style={styles.mediumCell}>
                            Fund / Credits
                          </DataTable.Title>
                          <DataTable.Title style={styles.narrowCell}>
                            Details
                          </DataTable.Title>
                          <DataTable.Title style={styles.mediumCell}>
                            Actions
                          </DataTable.Title>
                        </DataTable.Header>
                      )}
                      {!loading &&
                        !matched &&
                        unmatchedReservations.length === 0 && (
                          <DataTable.Row>
                            <DataTable.Cell>
                              No unmatched Guesty reservations found.
                            </DataTable.Cell>
                          </DataTable.Row>
                        )}
                      {!loading &&
                        matched &&
                        mmatchedReservations.length === 0 && (
                          <DataTable.Row>
                            <DataTable.Cell>
                              No matched Guesty reservations found.
                            </DataTable.Cell>
                          </DataTable.Row>
                        )}
                      {!loading &&
                        !matched &&
                        unmatchedReservations.length > 0 &&
                        unmatchedReservations.map(guestyReservation => (
                          <GuestyReservationRow
                            key={guestyReservation._id}
                            guestyReservation={guestyReservation}
                          />
                        ))}
                      {!loading &&
                        matched &&
                        mmatchedReservations.length > 0 &&
                        mmatchedReservations.map(guestyReservation => (
                          <GuestyReservationRow
                            key={guestyReservation._id}
                            guestyReservation={guestyReservation}
                          />
                        ))}
                    </DataTable>
                  </ScrollView>
                </Profiler>
              </List.Accordion>
            </>
          );
        })}

      {!matched && (
        <Profiler id="LP matching Portal" onRender={onRender}>
          <Portal>
            <Modal
              contentContainerStyle={themedStyles.modalContent}
              visible={editDialogVisible}
              onDismiss={() => setEditDialogVisible(false)}>
              <AlertsProvider>
                <View style={[styles.topContent, styles.center]}>
                  <Title>
                    Finding LP for Reservation {editReservation?._id} with
                    Guest: {editReservation?.guest?.fullName}
                  </Title>
                  {/** TODO - add info about which fund we're matching based on index vs payment map */}
                  <Text>
                    Guest Phone?: {editReservation?.guest?.phone}, Guest Email?{' '}
                    {editReservation?.guest?.email}
                  </Text>
                  <Searchbar
                    placeholder="Search"
                    onChangeText={onChangeSearch}
                    value={searchQuery}
                  />
                </View>
                <View style={styles.bottomContent}>
                  <ScrollView
                    persistentScrollbar={true}
                    contentContainerStyle={styles.modalScrollViewStyle}>
                    <DataTable>
                      <DataTable.Header>
                        <DataTable.Title>Limited Partner</DataTable.Title>
                        <DataTable.Title style={styles.smallCell}>
                          Save
                        </DataTable.Title>
                      </DataTable.Header>
                      {!shouldSearch(searchQuery) && (
                        <DataTable.Row>
                          <DataTable.Cell>
                            No search in effect. Type {minimumTextSearchString}{' '}
                            or more characters (or {minimumNumberSearchString}{' '}
                            or more numbers) above.
                          </DataTable.Cell>
                        </DataTable.Row>
                      )}
                      {shouldSearch(searchQuery) &&
                        searchedLimitedPartners?.length === 0 && (
                          <DataTable.Row>
                            <DataTable.Cell style={styles.center}>
                              No search results found
                            </DataTable.Cell>
                          </DataTable.Row>
                        )}
                      {(searchedLimitedPartners?.length ?? 0) > 0 &&
                        searchedLimitedPartners?.map(lp => (
                          <DataTable.Row key={lp.limitedPartnerId}>
                            <DataTable.Cell>
                              <LimitedPartnerComponent
                                limitedPartner={lp}
                                fund={limitedPartnerFundMap[lp.ref.id]}
                                // readOnly={true}
                              />
                            </DataTable.Cell>
                            <DataTable.Cell style={styles.smallCell}>
                              <Button
                                key="Save"
                                onPress={async () =>
                                  // FIXME send in the payment index to match vs reservation fund payment array for split txns
                                  saveLPReservationMatch(lp)
                                }>
                                Select
                              </Button>
                            </DataTable.Cell>
                          </DataTable.Row>
                        ))}
                    </DataTable>
                  </ScrollView>

                  <View style={styles.horizontalRight}>
                    <Button
                      key="Cancel"
                      onPress={() => setEditDialogVisible(false)}>
                      Cancel
                    </Button>
                  </View>
                </View>
              </AlertsProvider>
            </Modal>
          </Portal>
        </Profiler>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  horizontalPad: {paddingHorizontal: 10},
  row: {flexDirection: 'row'},
  content: {paddingHorizontal: 10},
  topContent: {
    flex: 0.4,
    paddingHorizontal: 20,
  },
  bottomContent: {
    flex: 1,
    paddingHorizontal: 20,
  },
  center: {
    // alignItems: 'center'
  },
  shortHeader: {height: 25},
  touchableRow: {
    paddingHorizontal: 5,
    flexDirection: 'row',
    // alignItems: 'center',
    // justifyContent: 'center',
  },
  widerCell: {
    // borderWidth: 1,
    // borderColor: 'red',
    minWidth: 200,
    // width: 150,
    justifyContent: 'center',
    alignItems: 'center',
  },
  smallCell: {
    maxWidth: 80,
    width: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mediumCell: {
    // minWidth: 60,
    // width: 60,
    maxWidth: 140,
    justifyContent: 'center',
    alignItems: 'center',
  },
  narrowCell: {
    // minWidth: 60,
    // width: 60,
    maxWidth: 110,
    justifyContent: 'center',
    alignItems: 'center',
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    position: 'relative',
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 5,
  },
  horizontalRight: {
    flexDirection: 'row',
    alignContent: 'flex-end',
    justifyContent: 'flex-end',
  },
  modalScrollViewStyle: {
    // borderWidth: 1,
    // borderColor: 'green',
    flex: 1,
    flexDirection: 'column',
  },
});

export default GuestyUnmatchedReservations;
